import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Link2, Plus, ExternalLink, Trash2, Edit2, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { CONTENT } from "@/constants/testIds";
import { useDepartments } from "@/hooks/useDepartments";

import { API } from "../lib/api";
const ax = () => axios.create({ withCredentials: true });

export default function UsefulLinksPage() {
  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole(["autor_area", "administrador_total", "admin"]);
  const { departments } = useDepartments();
  const DEPTS = departments;
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("__all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", url: "", department: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => ax().get(`${API}/useful-links`).then(r => setItems(r.data)).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const filtered = items.filter(i => {
    const matchSearch = i.title?.toLowerCase().includes(search.toLowerCase());
    const matchDept = deptFilter === "__all" || i.department === deptFilter || (!i.department && deptFilter === "__general");
    return matchSearch && matchDept;
  });

  const openCreate = () => { setEditing(null); setForm({ title: "", content: "", url: "", department: "" }); setShowForm(true); };
  const openEdit = (item) => { setEditing(item); setForm({ title: item.title, content: item.content, url: item.url || "", department: item.department || "" }); setShowForm(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await ax().put(`${API}/useful-links/${editing.item_id}`, form);
      else await ax().post(`${API}/useful-links`, form);
      setShowForm(false); load();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este link?")) return;
    await ax().delete(`${API}/useful-links/${id}`);
    load();
  };

  const byDept = filtered.reduce((acc, l) => {
    const key = l.department || "Geral";
    if (!acc[key]) acc[key] = [];
    acc[key].push(l);
    return acc;
  }, {});

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Link2 className="w-5 h-5 text-[#2346ff]" />
          <div>
            <h1 className="text-xl font-bold font-heading text-slate-900">Links Úteis</h1>
            <p className="text-sm text-slate-500">{items.length} links disponíveis</p>
          </div>
        </div>
        {canEdit && (
          <Button data-testid={CONTENT.createBtn} onClick={openCreate} style={{ backgroundColor: "#2346ff" }} className="text-white gap-2">
            <Plus className="w-4 h-4" /> Novo Link
          </Button>
        )}
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Buscar links..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Departamento" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Todos</SelectItem>
            <SelectItem value="__general">Geral</SelectItem>
            {DEPTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {[...Array(8)].map((_, i) => <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
      ) : (
        <div data-testid={CONTENT.contentList} className="space-y-6">
          {Object.entries(byDept).map(([dept, deptLinks]) => (
            <div key={dept}>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{dept}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {deptLinks.map(item => (
                  <div key={item.item_id} data-testid={CONTENT.contentItem} className="bento-card p-3 group flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#e8ecff" }}>
                      <Link2 className="w-4 h-4 text-[#2346ff]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{item.title}</p>
                      <p className="text-xs text-slate-400 truncate mt-0.5">{item.content}</p>
                      {item.url && (
                        <a href={item.url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-[#2346ff] hover:underline mt-1">
                          <ExternalLink className="w-3 h-3" /> Acessar
                        </a>
                      )}
                    </div>
                    {canEdit && (
                      <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 flex-shrink-0">
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEdit(item)}><Edit2 className="w-3 h-3" /></Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => remove(item.item_id)}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <Link2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum link encontrado</p>
            </div>
          )}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar Link" : "Novo Link Útil"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} /></div>
            <div><Label>URL *</Label><Input value={form.url} onChange={e => setForm(f => ({...f, url: e.target.value}))} placeholder="https://..." /></div>
            <div><Label>Descrição</Label><Textarea rows={2} value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))} /></div>
            <div>
              <Label>Departamento</Label>
              <Select value={form.department || "__none"} onValueChange={v => setForm(f => ({...f, department: v === "__none" ? "" : v}))}>
                <SelectTrigger><SelectValue placeholder="Geral" /></SelectTrigger>
                <SelectContent><SelectItem value="__none">Geral</SelectItem>{DEPTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.title || !form.url} style={{ backgroundColor: "#2346ff" }} className="text-white">{saving ? "Salvando..." : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
